import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useModules } from '../modules/ModuleContext';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { MobileDrawer } from './MobileDrawer';
import { AppDock } from './AppDock';
import { DockBadgesProvider } from './DockBadgesContext';
import { ChatFloatingButton } from '../../modules/stock/components/Chat/ChatFloatingButton';
import { StockChatPanel } from '../../modules/stock/components/Chat/StockChatPanel';
import PendingInvitationsBanner from '../../modules/zilloteam/components/PendingInvitationsBanner';
import { DemoModeBanner } from '../../components/ui/DemoModeBanner';
import { ZilloTeamSidebar } from '../../modules/zilloteam/components/ZilloTeamSidebar';
import { ZilloTasksSidebar } from '../../modules/zillotasks/components/ZilloTasksSidebar';
import { PartnerPreferencesProvider } from '../../modules/zilloteam/contexts/PartnerPreferencesContext';
import { api } from '../../lib/api';
import { setLocale } from '../../lib/i18n/i18n';

export function AppShell() {
  const { currentModule, currentSidebar } = useModules();
  const [chatOpen, setChatOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Load locale preference from DB on mount (authenticated context)
  useEffect(() => {
    api.getUserUiSetting<string>('locale').then(val => {
      if (val) setLocale(val);
    }).catch(() => {});
  }, []);

  const location = useLocation();
  const isHomeDashboard = location.pathname === '/';
  const isStockModule = currentModule?.id === 'stock';
  const isZilloTeamModule = currentModule?.id === 'zilloteam';
  const isZilloTasksModule = currentModule?.id === 'zillotasks';

  // beforeunload removed — was triggering on every refresh without checking for unsaved work

  return (
    <DockBadgesProvider>
      <div className="h-[100dvh] flex flex-col overflow-hidden bg-neutral-50 dark:bg-gray-900">
        <Header onMenuClick={() => setDrawerOpen(true)} />

        <MobileDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          currentModule={currentModule}
          sidebarConfig={currentSidebar}
        />

        {/* Demo mode banner */}
        <DemoModeBanner />

        {/* Global partnership invitation banner */}
        <PendingInvitationsBanner />

        <div className="flex flex-1 overflow-hidden">
          {/* App Dock — hidden on admin pages */}
          {currentModule?.id !== 'admin' && <AppDock />}

          {isHomeDashboard ? (
            <main className="flex-1 overflow-y-auto overflow-x-hidden">
              <div className="animate-fade-in h-full">
                <Outlet />
              </div>
            </main>
          ) : isZilloTeamModule ? (
            <PartnerPreferencesProvider>
              <ZilloTeamSidebar />
              <main className="flex-1 overflow-y-auto overflow-x-hidden">
                <div className="animate-fade-in h-full">
                  <Outlet />
                </div>
              </main>
            </PartnerPreferencesProvider>
          ) : isZilloTasksModule ? (
            <>
              <ZilloTasksSidebar />
              <main className="flex-1 overflow-y-auto overflow-x-hidden">
                <div className="animate-fade-in h-full">
                  <Outlet />
                </div>
              </main>
            </>
          ) : (
            <>
              {/* Sidebar — desktop only */}
              <Sidebar
                currentModule={currentModule}
                sidebarConfig={currentSidebar}
              />

              {/* Main content — scrollable container */}
              <main className="flex-1 overflow-y-auto overflow-x-hidden">
                <div className="animate-fade-in h-full">
                  <Outlet />
                </div>
              </main>
            </>
          )}
        </div>

        {/* Stock Chat — floating button + sliding panel (stock module only) */}
        {isStockModule && (
          <>
            <ChatFloatingButton
              isOpen={chatOpen}
              onToggle={() => setChatOpen((prev) => !prev)}
            />
            <StockChatPanel
              isOpen={chatOpen}
              onClose={() => setChatOpen(false)}
            />
          </>
        )}
      </div>
    </DockBadgesProvider>
  );
}

export default AppShell;
